import React, { useEffect, useState } from 'react';
import Popover from '@/components/ui/Popover';
import Tooltip from '@/components/ui/Tooltip';
import clsx from 'clsx';
import { twMerge } from '@/utils';
import cn from '@/utils/cn';
import Axios from 'axios';
import * as Routes from '@/modules/routes';
import useFlags from '@/hooks/useFlags';
import ShapeCategories from './ShapeCategories';
import Shapes from './Shapes';
import ImageCategory from './ImageCategory';
import Images from './Images';
import { imgStyle, isSelected, buttonStyle, scaleValue } from '../styles';
import useDrawingContext from '../../store/useDrawingContext';
import useShapesStore from './useShapesStore';
import icons from '../icons';

const popoverStyle = (toolbarEl: React.MutableRefObject<HTMLDivElement>) => ({
  left: '1.5rem',
  width: `${scaleValue(toolbarEl, 14.2, 16)}rem`,
  minHeight: `${scaleValue(toolbarEl, 267, 310)}px`,
});

const ShapesButton = () => {
  const toolbarEl = useDrawingContext(state => state.toolbarEl);
  const disabled = useDrawingContext(state => state.disabled);
  const mode = useDrawingContext(state => state.mode);
  const questionId = useDrawingContext(state => state.questionId);
  const userType = useDrawingContext(state => state.userType);
  const categoryMap = useShapesStore(state => state.categoryMap);
  const clipartCategories = Object.keys(categoryMap);
  const icon = icons.shapes;
  const [category, setCategory] = useState('');
  const [popoverIsOpen, setPopoverIsOpen] = useState(false);
  const [images, setImages] = useState([]);
  const [isLoadingImages, setIsLoadingImages] = useState(true);
  const builder = useDrawingContext(state => state.builder);
  const showImagesMenuItem = (userType !== 'Student' && builder) || images.length > 0;
  const { allowUploadingClipartDrawingQuestionImages } = useFlags();

  const buttonClassName = twMerge(
    'tw-bg-transparent tw-text-white tw-border-0 tw-cursor-pointer tw-p-2 tw-rounded tw-my-1',
    'hover:tw-bg-tci-purple tw-flex tw-items-center tw-justify-center',
    'disabled:tw-opacity-50 disabled:tw-cursor-not-allowed',
  );

  useEffect(() => {
    setIsLoadingImages(true);

    const fetchImages = async () => {
      try {
        const { data } = await Axios.get(Routes.plato_api_image_models_path(
          { cliparts: true, for_model: { model_id: questionId, model_type: 'Question' } },
        ));

        setImages(data.data.map(imageModel => imageModel.image));
      } catch (error) {
        throw error;
      }
    };

    fetchImages();
    setIsLoadingImages(false);
  }, []);

  return (
    <Tooltip.Provider>
      <Tooltip>
        <Popover
          open={popoverIsOpen}
          onOpenChange={(isOpen) => {
            setPopoverIsOpen(isOpen);
            if (!isOpen) setCategory('');
          }}
        >
          <Popover.Anchor />
          <Tooltip.Trigger asChild>
            <Popover.Trigger
              className={clsx(
                buttonClassName, {
                  'tw-bg-tci-purple': isSelected(mode, 'shape', disabled),
                },
              )}
              disabled={disabled}
              style={buttonStyle(toolbarEl)}
              type="button"
            >
              <img
                alt={icon.alt}
                src={icon.icon}
                style={imgStyle({ toolbarEl })}
              />
            </Popover.Trigger>
          </Tooltip.Trigger>
          <Popover.Content
            aria-label="Shape categories"
            className="tw-absolute tw-top-1 tw-overflow-visible"
            style={popoverStyle(toolbarEl)}
          >
            {showImagesMenuItem && allowUploadingClipartDrawingQuestionImages && (
              <>
                <ImageCategory
                  category={category}
                  setCategory={setCategory}
                />
                {category === 'Images' && (
                  <Images
                    category={category}
                    images={images}
                    isLoadingImages={isLoadingImages}
                    questionId={questionId}
                    setCategory={setCategory}
                    setImages={setImages}
                    setPopoverIsOpen={setPopoverIsOpen}
                    userType={userType}
                  />
                )}
              </>
            )}
            <ShapeCategories
              category={category}
              setCategory={setCategory}
            />
            {clipartCategories.includes(category) && (
              <Shapes
                category={category}
                setCategory={setCategory}
                setPopoverIsOpen={setPopoverIsOpen}
              />
            )}
          </Popover.Content>
          <Tooltip.Content
            className={cn(
              'tw-bg-white tw-shadow tw-rounded-sm tw-text-sm tw-py-1 tw-px-2 tw-outline tw-outline-1',
              'tw-outline-gray-300',
            )}
            side="right"
            sideOffset={10}
          >
            {icon.alt}
          </Tooltip.Content>
        </Popover>
      </Tooltip>
    </Tooltip.Provider>
  );
};

export default ShapesButton;
